import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import SEO from "./SEO.jsx";
import "./Whoweare.css";

const Whoweare = () => {
  const { t } = useTranslation();
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("ww-visible");
          }
        });
      },
      { threshold: 0.12 }
    );
    sectionsRef.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addRef = (el) => {
    if (el && !sectionsRef.current.includes(el)) sectionsRef.current.push(el);
  };

  return (
    <>
      <SEO
        titleKey="pages.seo.whoweare.title"
        descriptionKey="pages.seo.whoweare.description"
        keywordsKey="pages.seo.whoweare.keywords"
        path="/whoweare"
        breadcrumbLabel="Who We Are"
      />

      <div className="ww-root">
        <div className="ww-bg-blob ww-bg-blob-1" aria-hidden="true" />
        <div className="ww-bg-blob ww-bg-blob-2" aria-hidden="true" />

        <div className="ww-content">

          {/* ── HERO (replaces History section) ── */}
          <section className="ww-hero">
            <div className="ww-hero-inner">
              <span className="ww-hero-label ww-fade" ref={addRef}>{t('pages.whoWeAre.heroLabel')}</span>
              <h1 className="ww-fade ww-delay-1" ref={addRef}>
                {t('pages.whoWeAre.heroTitle1')}<br />{t('pages.whoWeAre.heroTitle2')}
              </h1>
              <p className="ww-fade ww-delay-2" ref={addRef}>
                {t('pages.whoWeAre.heroBody')}
              </p>
              <button className="ww-hero-btn ww-fade ww-delay-3" ref={addRef}>
                {t('pages.whoWeAre.seeMore')}
              </button>
            </div>
          </section>

          {/* ── OVERVIEW ── */}
          <section className="ww-section-full">
            <div className="ww-section-inner">
              <div className="ww-panel ww-fade" ref={addRef}>
                <div className="ww-two-col">
                  <div className="ww-fade-left" ref={addRef}>
                    <span className="ww-label">{t('pages.whoWeAre.sectionLabel')}</span>
                    <h2 className="ww-h1">{t('pages.whoWeAre.overviewTitle1')}<br /><em>{t('pages.whoWeAre.overviewTitle2')}</em></h2>
                    <p className="ww-body">
                      {t('pages.whoWeAre.overviewP1')}
                    </p>
                    <p className="ww-body" style={{ marginTop: 16 }}>
                      {t('pages.whoWeAre.overviewP2')}
                    </p>
                  </div>
                  <div className="ww-fade-right" ref={addRef} style={{ position: 'relative' }}>
                    <div className="ww-img-frame">
                      <img src="/nkarner/Screenshot 2025-10-18 192557.png" alt="Greenwich Logo" />
                    </div>
                    <div className="ww-img-accent" />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="ww-divider" />

          {/* ── STATS band ── */}
          <section style={{ padding: '0 clamp(20px,5vw,80px)', maxWidth: 1280, margin: '0 auto' }}>
            <div className="ww-stats ww-fade" ref={addRef} style={{ margin: '48px 0' }}>
              {[
                { num: '14-35', label: t('pages.whoWeAre.statAge') },
                { num: '2025',  label: t('pages.whoWeAre.statYear') },
                { num: '100+',  label: t('pages.whoWeAre.statOpp') },
              ].map((s, i) => (
                <div key={i} className="ww-stat">
                  <div className="ww-stat-num">{s.num}</div>
                  <div className="ww-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* ── WHY GREENWICH ── */}
          <section className="ww-section-full">
            <div className="ww-section-inner">
              <div className="ww-panel">
                <div className="ww-why-intro">
                  <span className="ww-label ww-fade" ref={addRef}>{t('pages.whoWeAre.whyLabel')}</span>
                  <h2 className="ww-h1 ww-fade ww-delay-1" ref={addRef}>
                    {t('pages.whoWeAre.whyTitle1')}<br />{t('pages.whoWeAre.whyTitle2')}
                  </h2>
                  <p className="ww-body ww-fade ww-delay-2" ref={addRef}>
                    {t('pages.whoWeAre.whyBody')}
                  </p>
                </div>
                <div className="ww-cards-row">
                  {[
                    { src: '/nkarner/region-america.webp', label: t('pages.whoWeAre.regionAmerica') },
                    { src: '/nkarner/region-asia.webp', label: t('pages.whoWeAre.regionAsia') },
                    { src: '/nkarner/region-europe.webp', label: t('pages.whoWeAre.regionEurope') },
                  ].map((card, i) => (
                    <div key={i} className={`ww-region-card ww-fade ww-delay-${i + 1}`} ref={addRef}>
                      <img src={card.src} alt={card.label} />
                      <div className="ww-region-card-overlay">
                        <span className="ww-region-card-label">{card.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ── QUOTE band ── */}
          <section className="ww-section-inner" style={{ padding: '0 clamp(20px,5vw,80px)' }}>
            <div className="ww-quote-band ww-fade" ref={addRef}>
              <blockquote>
                {t('pages.whoWeAre.quote')}
              </blockquote>
            </div>
          </section>

          {/* ── VISION ── */}
          <section className="ww-section-full">
            <div className="ww-section-inner">
              <div className="ww-panel">
                <div className="ww-two-col ww-reverse-mobile">
                  <div className="ww-fade-right" ref={addRef} style={{ position: 'relative' }}>
                    <div className="ww-img-square">
                      <img src="/nkarner/257ba1adee1b54abd49ac6a8c0f49ea9.png" alt="Vision" />
                    </div>
                  </div>
                  <div className="ww-fade-left" ref={addRef}>
                    <span className="ww-label">{t('pages.whoWeAre.visionLabel')}</span>
                    <h2 className="ww-h1">{t('pages.whoWeAre.visionTitle1')} <em>{t('pages.whoWeAre.visionTitle2')}</em> {t('pages.whoWeAre.visionTitle3')}<br />{t('pages.whoWeAre.visionTitle4')}</h2>
                    <p className="ww-body">
                      {t('pages.whoWeAre.visionBody')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="ww-divider" />

          {/* ── MISSION ── */}
          <section className="ww-section-full">
            <div className="ww-section-inner">
              <div className="ww-panel">
                <div className="ww-two-col">
                  <div className="ww-fade-left" ref={addRef}>
                    <span className="ww-label">{t('pages.whoWeAre.missionLabel')}</span>
                    <h2 className="ww-h1">{t('pages.whoWeAre.missionTitle1')}<br /><em>{t('pages.whoWeAre.missionTitle2')}</em></h2>
                    <p className="ww-body">
                      {t('pages.whoWeAre.missionBody1')}
                    </p>
                    <p className="ww-body" style={{ marginTop: 16 }}>
                      {t('pages.whoWeAre.missionBody2')}
                    </p>
                  </div>
                  <div className="ww-fade-right" ref={addRef} style={{ position: 'relative' }}>
                    <div className="ww-img-square">
                      <img src="/nkarner/mission-icon.png" alt="Mission" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── SDG / PROMOTING ── */}
          <section className="ww-section-full">
            <div className="ww-section-inner">
              <div className="ww-panel">
                <div className="ww-two-col ww-reverse-mobile">
                  <div className="ww-fade-right" ref={addRef}>
                    <div className="ww-img-frame" style={{ aspectRatio: '1/1' }}>
                      <img src="/nkarner/Screenshot 2025-10-18 200417.png" alt="SDG" />
                    </div>
                  </div>
                  <div className="ww-fade-left" ref={addRef}>
                    <span className="ww-label">{t('pages.whoWeAre.sdgLabel')}</span>
                    <h2 className="ww-h1">{t('pages.whoWeAre.sdgTitle1')}<br /><em>{t('pages.whoWeAre.sdgTitle2')}</em></h2>
                    <p className="ww-body">
                      {t('pages.whoWeAre.sdgBody')}
                    </p>
                    <div className="ww-sdg-chips ww-fade ww-delay-1" ref={addRef}>
                      {[t('pages.whoWeAre.sdgChip1'), t('pages.whoWeAre.sdgChip2'), t('pages.whoWeAre.sdgChip3'), t('pages.whoWeAre.sdgChip4'), t('pages.whoWeAre.sdgChip5')].map((chip, i) => (
                        <span key={i} className="ww-chip">{chip}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── BOTTOM CTA ── */}
          <section className="ww-cta">
            <div className="ww-fade ww-cta-inner" ref={addRef}>
              <h2 className="ww-cta-title">
                {t('pages.whoWeAre.ctaTitle1')}<br />{t('pages.whoWeAre.ctaTitle2')}
              </h2>
              <p className="ww-cta-sub">
                {t('pages.whoWeAre.ctaSub')}
              </p>
              <button className="ww-hero-btn">
                {t('pages.whoWeAre.getStarted')}
              </button>
            </div>
          </section>

        </div>
      </div>
    </>
  );
};

export default Whoweare;
